import { apiBinaryRequest, apiRequest } from './apiClient';

const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }
    query.set(key, String(value));
  });
  return query.toString();
};

const readCommandValue = (message = {}, data = {}, keys = []) => {
  for (const key of keys) {
    const dataValue = data[key];
    if (dataValue !== null && dataValue !== undefined && dataValue !== '') {
      return dataValue;
    }
    const rootValue = message[key];
    if (rootValue !== null && rootValue !== undefined && rootValue !== '') {
      return rootValue;
    }
  }
  return undefined;
};

const readCommandValueAllowEmpty = (message = {}, data = {}, keys = []) => {
  for (const key of keys) {
    if (data[key] !== null && data[key] !== undefined) {
      return data[key];
    }
    if (message[key] !== null && message[key] !== undefined) {
      return message[key];
    }
  }
  return undefined;
};

const toPositiveInteger = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const coerceBooleanFlag = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

const arrayBufferToBase64 = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return window.btoa(binary);
};

const parseContentDispositionFilename = (contentDisposition = '') => {
  const filenameStarMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch (error) {
      return filenameStarMatch[1];
    }
  }

  const filenameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return filenameMatch?.[1] || '';
};

const binaryResponseToCommandPayload = async (response, fallbackFilename) => {
  const arrayBuffer = await response.arrayBuffer();
  const count = response.headers.get('X-Export-Count');
  const sheets = response.headers.get('X-Export-Sheets');
  const filename = response.headers.get('X-Export-Filename')
    || parseContentDispositionFilename(response.headers.get('Content-Disposition') || '')
    || fallbackFilename;

  const payload = {
    status: 'success',
    filename,
    data: arrayBufferToBase64(arrayBuffer),
  };

  const parsedCount = Number.parseInt(count, 10);
  if (Number.isInteger(parsedCount)) {
    payload.count = parsedCount;
  }

  const parsedSheets = Number.parseInt(sheets, 10);
  if (Number.isInteger(parsedSheets)) {
    payload.sheets = parsedSheets;
  }

  return payload;
};

const fetchProjects = async (payload = {}) => {
  const query = buildQueryString({
    scope_picker: payload.scope_picker ? 'true' : '',
    search: payload.search || '',
    page: Number.isInteger(payload.page) ? payload.page : 0,
    limit: Number.isInteger(payload.limit) ? payload.limit : '',
    selected_ids: Array.isArray(payload.selected_ids) ? payload.selected_ids.join(',') : '',
    collection_id: Number.isInteger(payload.collection_id) ? payload.collection_id : '',
  });
  const response = await apiRequest(`/api/v1/projects${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const fetchProject = async (caseId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}`);
  return normalizeGetcasePayload(response.data ?? response);
};

const updateProject = async (caseId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return response.data ?? response;
};

const fetchProjectWidgets = async (caseId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets`);
  const payload = response.data ?? response;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return { ...payload, caseid: payload.caseid ?? caseId };
  }
  return payload;
};

const fetchDashboardSnapshots = async (caseId, payload = {}) => {
  const query = buildQueryString({
    limit: toPositiveInteger(payload.limit) || '',
  });
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/dashboard-snapshots${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const saveDashboardSnapshot = async (caseId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/dashboard-snapshots`, {
    method: 'POST',
    body: JSON.stringify({
      comment: payload.comment || '',
    }),
  });
  return response.data ?? response;
};

const loadDashboardSnapshot = async (caseId, snapshotId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/dashboard-snapshots/${encodeURIComponent(snapshotId)}`);
  return response.data ?? response;
};

const saveWidgetPreferences = async (caseId, widgetId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/${encodeURIComponent(widgetId)}/preferences`, {
    method: 'POST',
    body: JSON.stringify({
      preferences: payload.preferences && typeof payload.preferences === 'object' ? payload.preferences : {},
      height: payload.height,
    }),
  });
  return response.data ?? response;
};

export const saveWidgetResponse = async (caseId, widgetId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/${encodeURIComponent(widgetId)}/response`, {
    method: 'POST',
    body: JSON.stringify({
      response: payload.response,
    }),
  });
  return response.data ?? response;
};

const saveWidgetOrder = async (caseId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/order`, {
    method: 'POST',
    body: JSON.stringify({
      order: payload.order,
      widget_order: payload.widget_order,
      widgetOrder: payload.widgetOrder,
      widget_ids: payload.widget_ids,
      layout_order: payload.layout_order,
      layoutOrder: payload.layoutOrder,
      layout_settings: payload.layout_settings,
      layoutSettings: payload.layoutSettings,
    }),
  });
  return response.data ?? response;
};

const addWidget = async (caseId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets`, {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      caseid: caseId,
    }),
  });
  return response.data ?? response;
};

const updateWidget = async (caseId, widgetId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/${encodeURIComponent(widgetId)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      ...payload,
      caseid: caseId,
      widget_id: widgetId,
    }),
  });
  return response.data ?? response;
};

const deleteWidget = async (caseId, widgetId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/${encodeURIComponent(widgetId)}`, {
    method: 'DELETE',
  });
  return response.data ?? response;
};

const reanalyzeWidget = async (caseId, widgetId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/${encodeURIComponent(widgetId)}/reanalyze`, {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      caseid: caseId,
      widget_id: widgetId,
    }),
  });
  return response.data ?? response;
};

const rebuildWidgetFromProofs = async (caseId, widgetId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/${encodeURIComponent(widgetId)}/rebuild-ui`, {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      caseid: caseId,
      widget_id: widgetId,
      rebuild_ui: payload.rebuild_ui ?? true,
    }),
  });
  return response.data ?? response;
};

const revertWidgetSnapshot = async (caseId, widgetId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/${encodeURIComponent(widgetId)}/revert-snapshot`, {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      caseid: caseId,
      widget_id: widgetId,
    }),
  });
  return response.data ?? response;
};

const applyWidgetFeedback = async (caseId, widgetId, payload = {}) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/widgets/${encodeURIComponent(widgetId)}/feedback`, {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      caseid: caseId,
      widget_id: widgetId,
    }),
  });
  return response.data ?? response;
};

const fetchProjectExport = async (caseId, exportPath, body = {}, fallbackFilename = 'export') => {
  const response = await apiBinaryRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/exports/${exportPath}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return binaryResponseToCommandPayload(response, fallbackFilename);
};

const downloadProjectUploadFiles = async (caseId) => {
  const response = await apiBinaryRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/files/download-all`);
  return binaryResponseToCommandPayload(response, 'project_files.zip');
};

export const normalizeGetcasePayload = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const casePayload = payload.case && typeof payload.case === 'object' ? payload.case : {};
  return {
    ...payload,
    access_mode: payload.access_mode ?? casePayload.access_mode,
    can_read: payload.can_read ?? casePayload.can_read,
    can_upload: payload.can_upload ?? casePayload.can_upload,
  };
};

const fetchProjectTags = async (caseId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/tags`);
  return response.data ?? response;
};

const addProjectToCollection = async (caseId, collectionId) => {
  const response = await apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/collections/${encodeURIComponent(collectionId)}`,
    {
      method: 'POST',
    }
  );
  return response.data ?? response;
};

const removeProjectFromCollection = async (caseId, collectionId) => {
  const response = await apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/collections/${encodeURIComponent(collectionId)}`,
    {
      method: 'DELETE',
    }
  );
  return response.data ?? response;
};

const fetchAllUniqueTags = async (caseId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/tags/all`);
  return response.data ?? response;
};

const fetchGenericDatasources = async (caseId, connectorType) => {
  const query = buildQueryString({ type: connectorType || '' });
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/datasources${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const fetchAllGenericDatasources = async (caseId, connectorTypes = []) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/datasources/batch`, {
    method: 'POST',
    body: JSON.stringify({
      types: Array.isArray(connectorTypes) ? connectorTypes : [],
    }),
  });
  return response.data ?? response;
};

const fetchDatasourceSuggestions = async (caseId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/datasource-suggestions`);
  return response.data ?? response;
};

const fetchConnectorStatus = async (caseId, connectorType, datasourceId) => {
  const query = buildQueryString({
    type: connectorType || '',
    datasource_id: datasourceId,
  });
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/connector/status${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const fetchConnectorData = async (caseId, connectorType, datasourceId, forceNew = false) => {
  const query = buildQueryString({
    type: connectorType || '',
    datasource_id: datasourceId,
    force_new: forceNew ? '1' : '',
  });
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/connector/data${query ? `?${query}` : ''}`);
  return response;
};

const fetchDatasourceIngestions = async (caseId, datasourceId, connectorType, limit) => {
  const query = buildQueryString({
    type: connectorType || '',
    limit,
  });
  const response = await apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/datasources/${encodeURIComponent(datasourceId)}/ingestions${query ? `?${query}` : ''}`
  );
  return response.data ?? response;
};

const fetchDatasourceMonitoringStatus = async (caseId, datasourceId, connectorType) => {
  const query = buildQueryString({ type: connectorType || '' });
  const response = await apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/datasources/${encodeURIComponent(datasourceId)}/monitoring-status${query ? `?${query}` : ''}`
  );
  return response.data ?? response;
};

const fetchActionPlugins = async () => {
  const response = await apiRequest('/api/v1/action-plugins');
  return response.data ?? response;
};

const fetchProjectActionExportSettings = async (caseId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/actions/export-settings`);
  return response.data ?? response;
};

const fetchSingleProjectActionExportSettings = async (caseId, exportId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/actions/export-settings/${encodeURIComponent(exportId)}`);
  return response.data ?? response;
};

const fetchFoldersSettings = async (caseId) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/folders/settings`);
  return response.data ?? response;
};

const updateFoldersEnabled = async (caseId, enable) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/folders/enabled`, {
    method: 'POST',
    body: JSON.stringify({ enable }),
  });
  return response.data ?? response;
};

const saveFoldersSettings = async (caseId, folders = []) => {
  const response = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/folders/settings`, {
    method: 'POST',
    body: JSON.stringify({ folders: Array.isArray(folders) ? folders : [] }),
  });
  return response.data ?? response;
};

const fetchTemplates = async () => {
  const response = await apiRequest('/api/v1/templates');
  return response.data ?? response;
};

const saveTemplate = async (payload = {}) => {
  const response = await apiRequest('/api/v1/templates', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data ?? response;
};

const fetchTemplate = async (templateId) => {
  const response = await apiRequest(`/api/v1/templates/${encodeURIComponent(templateId)}`);
  return response.data ?? response;
};

const deleteTemplate = async (templateId) => {
  const response = await apiRequest(`/api/v1/templates/${encodeURIComponent(templateId)}`, {
    method: 'DELETE',
  });
  return response.data ?? response;
};

const importTemplate = async (payload = {}) => {
  const response = await apiRequest('/api/v1/templates/import', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data ?? response;
};

const importTemplates = async (payload = {}) => {
  const response = await apiRequest('/api/v1/templates/import-batch', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data ?? response;
};

const createProjectFromTemplate = async (templateId, payload = {}) => {
  const response = await apiRequest(
    `/api/v1/templates/${encodeURIComponent(templateId)}/projects`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return response.data ?? response;
};

const updateTimeevent = async (eventId, payload = {}) => {
  const response = await apiRequest(`/api/v1/timeevents/${encodeURIComponent(eventId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return response.data ?? response;
};

const deleteTimeevent = async (eventId) => {
  const response = await apiRequest(`/api/v1/timeevents/${encodeURIComponent(eventId)}`, {
    method: 'DELETE',
  });
  return response.data ?? response;
};

const reanalyzeProjectErrorTimeevents = async (caseId) => {
  const response = await apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/timeevents/reanalyze-errors`,
    { method: 'POST' }
  );
  return response.data ?? response;
};

const linkProjectAsTimeevent = async (caseId, linkedCaseId) => {
  const response = await apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/linked-timeevents`,
    {
      method: 'POST',
      body: JSON.stringify({ linkedCaseId }),
    }
  );
  return response.data ?? response;
};

const fetchConfigurationData = async () => {
  const response = await apiRequest('/api/v1/settings/configuration');
  return response.data ?? response;
};

const updateConfigurationData = async (payload = {}) => {
  const response = await apiRequest('/api/v1/settings/configuration', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.data ?? response;
};

export const fetchSavedModels = async () => {
  const response = await apiRequest('/api/v1/settings/saved-models');
  return response.data ?? response;
};

const fetchServerInfo = async (payload = {}) => {
  const forceReload = coerceBooleanFlag(payload.force_reload ?? payload.forceReload);
  const query = buildQueryString({
    force_reload: forceReload ? '1' : '',
  });
  const response = await apiRequest(`/api/v1/server-info${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const fetchOllamaModels = async () => {
  const response = await apiRequest('/api/v1/settings/ollama-models');
  return response.data ?? response;
};

const fetchAuthStatus = async () => {
  const response = await apiRequest('/api/v1/auth/status');
  return response.data ?? response;
};

const validateSession = async (payload = {}) => {
  const response = await apiRequest('/api/v1/auth/validate-session', {
    method: 'POST',
    body: JSON.stringify({
      session_token: payload.session_token || payload.sessionToken || '',
    }),
  });
  return response.data ?? response;
};

const fetchAccountMfaStatus = async () => {
  const response = await apiRequest('/api/v1/account/mfa-status');
  return response.data ?? response;
};

const fetchAdminUsers = async () => {
  const response = await apiRequest('/api/v1/admin/users');
  return response.data ?? response;
};

const fetchGlobalMfaRequirement = async () => {
  const response = await apiRequest('/api/v1/admin/mfa-requirement');
  return response.data ?? response;
};

const updateGlobalMfaRequirement = async (payload = {}) => {
  const response = await apiRequest('/api/v1/admin/mfa-requirement', {
    method: 'POST',
    body: JSON.stringify({ required: payload.required }),
  });
  return response.data ?? response;
};

const fetchLoginActivity = async (payload = {}) => {
  const query = buildQueryString({
    cursor: payload.cursor ?? '',
    limit: Number.isInteger(payload.limit) ? payload.limit : '',
    request_id: payload.request_id || '',
  });
  const response = await apiRequest(`/api/v1/admin/login-activity${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const fetchGlobalAiTokenUsage = async (payload = {}) => {
  const query = buildQueryString({
    group_by: payload.group_by || payload.groupBy || 'month',
    start_date: payload.start_date || payload.startDate || '',
    end_date: payload.end_date || payload.endDate || '',
  });
  const response = await apiRequest(`/api/v1/admin/ai-token-usage${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const fetchAdminLogs = async (payload = {}) => {
  const query = buildQueryString({
    limit: Number.isInteger(payload.limit) ? payload.limit : '',
    log_file: payload.log_file || payload.selected_log || payload.selectedLog || 'all',
    request_id: payload.request_id || payload.requestId || '',
  });
  const response = await apiRequest(`/api/v1/admin/logs${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const fetchAdminActiveTasksInfo = async (payload = {}) => {
  const query = buildQueryString({
    max_queue_files: Number.isInteger(payload.max_queue_files) ? payload.max_queue_files : '',
    max_progress_files: Number.isInteger(payload.max_progress_files) ? payload.max_progress_files : '',
  });
  const response = await apiRequest(`/api/v1/admin/active-tasks${query ? `?${query}` : ''}`);
  return response.data ?? response;
};

const fetchPostprocessingStatus = async () => {
  const response = await apiRequest('/api/v1/status/postprocessing');
  return response.data ?? response;
};

const HTTP_BRIDGE_RESPONSE_COMMANDS = {
  check_login_status: 'check_login_status',
  validate_session: 'session_validated',
  get_mfa_status: 'get_mfa_status',
  get_all_users: 'get_all_users',
  get_global_mfa_requirement: 'get_global_mfa_requirement',
  set_global_mfa_requirement: 'set_global_mfa_requirement',
  get_login_activity: 'get_login_activity_response',
  getcases: 'listcases',
  getcase: 'getcase',
  getWidgets: 'getWidgets',
  getDashboardSnapshots: 'getDashboardSnapshots',
  saveDashboardSnapshot: 'saveDashboardSnapshot',
  loadDashboardSnapshot: 'loadDashboardSnapshot',
  addWidget: 'addWidget',
  updateWidget: 'updateWidget',
  deleteWidget: 'deleteWidget',
  reanalyzeWidget: 'reanalyzeWidget',
  rebuildWidgetFromProofs: 'reanalyzeWidget',
  revertWidgetSnapshot: 'revertWidgetSnapshot',
  applyWidgetFeedback: 'reanalyzeWidget',
  saveWidgetPreferences: 'saveWidgetPreferences',
  saveWidgetOrder: 'saveWidgetOrder',
  downloadAllProjectUploadFiles: 'downloadAllProjectUploadFiles',
  exportexcel_from_variable: 'exportexcel_from_variable',
  exportexcel_all_variables: 'exportexcel_all_variables',
  exportexcel: 'exportexcel',
  exportcsv: 'exportcsv',
  exportpdf: 'exportpdf',
  exportjson: 'exportjson',
  get_project_tags: 'project_tags',
  get_all_unique_tags: 'get_all_unique_tags',
  get_server_info: 'server_info',
  get_saved_models: 'saved_models',
  get_text_model_settings: 'get_text_model_settings',
  get_vision_model_settings: 'get_vision_model_settings',
  get_audio_model_settings: 'get_audio_model_settings',
  get_performance_settings: 'get_performance_settings',
  get_format_settings: 'get_format_settings',
  get_available_ollama_models: 'available_ollama_models',
  configuration_check: 'configuration_data',
  update_configuration: 'update_configuration',
  get_active_tasks_info: 'active_tasks_info',
  get_postprocessing_status: 'postprocessing_status',
  get_logs: 'get_logs_response',
  get_global_ai_token_usage: 'get_global_ai_token_usage',
  listGenericDatasources: 'listGenericDatasources',
  listAllGenericDatasources: 'listGenericDatasources',
  getGenericSourceConnectorStatus: 'getGenericSourceConnectorStatus',
  getGenericSourceConnectorData: 'getGenericSourceConnectorData',
  getDatasourceIngestions: 'getDatasourceIngestions',
  getDatasourceMonitoringStatus: 'getDatasourceMonitoringStatus',
  getActionPlugins: 'getActionPlugins',
  getListExportsSettings: 'getListExportSettings',
  getExportSettings: 'getExportSettings',
  getDatasourceSuggestions: 'getDatasourceSuggestions',
  getFoldersSettings: 'getFoldersSettings',
  enableFolders: 'enableFolders',
  saveFoldersSettings: 'saveFoldersSettings',
  addProjectToCollection: 'addProjectToCollection',
  removeProjectFromCollection: 'removeProjectFromCollection',
  update_case_public: 'update_case_public_response',
};

export const getHttpBridgeResponseCommand = (command) => HTTP_BRIDGE_RESPONSE_COMMANDS[command] || command;

export const executeHttpCommandBridge = async (messageToSend = {}) => {
  const command = messageToSend.command;
  const data = messageToSend.data || {};

  switch (command) {
    case 'check_login_status':
      return { command: 'check_login_status', data: await fetchAuthStatus() };
    case 'validate_session':
      return { command: 'session_validated', data: await validateSession(data) };
    case 'get_mfa_status':
      return { command: 'get_mfa_status', data: await fetchAccountMfaStatus() };
    case 'get_all_users':
      return { command: 'get_all_users', data: await fetchAdminUsers() };
    case 'get_global_mfa_requirement':
      return { command: 'get_global_mfa_requirement', data: await fetchGlobalMfaRequirement() };
    case 'set_global_mfa_requirement':
      return { command: 'set_global_mfa_requirement', data: await updateGlobalMfaRequirement(data) };
    case 'get_login_activity':
      return { command: 'get_login_activity_response', data: await fetchLoginActivity(data) };
    case 'get_global_ai_token_usage':
      return { command: 'get_global_ai_token_usage', data: await fetchGlobalAiTokenUsage(data) };
    case 'getcases':
      return { command: 'listcases', data: await fetchProjects(data) };
    case 'getcase':
      return { command: 'getcase', data: await fetchProject(data.caseid ?? messageToSend.caseid) };
    case 'changetitle': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      const result = await updateProject(caseId, {
        title: readCommandValueAllowEmpty(messageToSend, data, ['title']),
      });
      return { command: 'changetitle', data: result.title };
    }
    case 'changedescription': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      const result = await updateProject(caseId, {
        description: readCommandValueAllowEmpty(messageToSend, data, ['description']),
      });
      return { command: 'changedescription', data: result.description };
    }
    case 'update_case_public': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command: 'update_case_public_response',
        data: await updateProject(caseId, {
          isPublic: readCommandValue(messageToSend, data, ['isPublic', 'is_public']),
          publicAccessLevel: readCommandValue(messageToSend, data, ['publicAccessLevel', 'public_access_level']) || 'view',
        }),
      };
    }
    case 'toggleCaseIngestionPause': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command: 'toggleCaseIngestionPause',
        data: await updateProject(caseId, {
          ingestionPaused: readCommandValue(messageToSend, data, ['paused', 'ingestionPaused']),
        }),
      };
    }
    case 'updateOtherEnableMultiDocsSplit': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          enableMultiDocsSplit: readCommandValue(messageToSend, data, ['enable_multi_docs_split', 'enableMultiDocsSplit']),
        }),
      };
    }
    case 'updateOtherEnableProcessOnFileChange': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          processOnFileChange: readCommandValue(messageToSend, data, ['enable_process_on_file_change', 'processOnFileChange']),
        }),
      };
    }
    case 'updateOtherPreferImageCreationTime': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          preferImageCreationTime: readCommandValue(messageToSend, data, ['prefer_image_creation_time', 'preferImageCreationTime']),
        }),
      };
    }
    case 'updateOtherSkipInitialAiSetup': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          skipInitialAiSetup: readCommandValue(messageToSend, data, ['skip_initial_ai_setup', 'skipInitialAiSetup']),
        }),
      };
    }
    case 'updateOtherProjectPdfAutoRotatePreset': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          projectPdfAutoRotatePreset: readCommandValue(messageToSend, data, ['project_pdf_auto_rotate_preset', 'projectPdfAutoRotatePreset']),
        }),
      };
    }
    case 'updateOtherProjectPdfAutoRotateUseEasyocr': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          projectPdfAutoRotateUseEasyocr: readCommandValue(messageToSend, data, ['project_pdf_auto_rotate_use_easyocr', 'projectPdfAutoRotateUseEasyocr']),
        }),
      };
    }
    case 'updateOtherShowCalendarWidget': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          showCalendarWidget: readCommandValue(messageToSend, data, ['show_calendar_widget', 'showCalendarWidget']),
        }),
      };
    }
    case 'updateOtherShowTrendWidget': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          showTrendWidget: readCommandValue(messageToSend, data, ['show_trend_widget', 'showTrendWidget']),
        }),
      };
    }
    case 'updateOtherFileIngestionFilter': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          fileIngestionFilters: readCommandValue(messageToSend, data, ['file_filters', 'fileIngestionFilters']) || [],
        }),
      };
    }
    case 'update_project_model_provider': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command,
        data: await updateProject(caseId, {
          project_model_provider: readCommandValue(messageToSend, data, ['project_model_provider']) || '',
          project_text_model_provider: readCommandValue(messageToSend, data, ['project_text_model_provider']) || '',
          project_text_model_id: readCommandValue(messageToSend, data, ['project_text_model_id']) || '',
          project_vision_model_provider: readCommandValue(messageToSend, data, ['project_vision_model_provider']) || '',
          project_vision_model_id: readCommandValue(messageToSend, data, ['project_vision_model_id']) || '',
          project_audio_model_provider: readCommandValue(messageToSend, data, ['project_audio_model_provider']) || '',
          project_audio_model_id: readCommandValue(messageToSend, data, ['project_audio_model_id']) || '',
        }),
      };
    }
    case 'getAllTemplates':
      return { command: 'getAllTemplates', data: await fetchTemplates() };
    case 'saveTemplate':
      return {
        command: 'saveTemplate',
        data: await saveTemplate({
          case_id: readCommandValue(messageToSend, data, ['case_id', 'caseid', 'caseId']),
          template_title: readCommandValue(messageToSend, data, ['template_title', 'templateTitle']),
          template_description: readCommandValue(messageToSend, data, ['template_description', 'templateDescription']) || '',
          version: readCommandValue(messageToSend, data, ['version']) ?? 1,
        }),
      };
    case 'loadTemplate': {
      const templateId = readCommandValue(messageToSend, data, ['template_id', 'templateId']);
      return { command: 'loadTemplate', data: await fetchTemplate(templateId) };
    }
    case 'deleteTemplate': {
      const templateId = readCommandValue(messageToSend, data, ['template_id', 'templateId']);
      return { command: 'deleteTemplate', data: await deleteTemplate(templateId) };
    }
    case 'importTemplate':
      return {
        command: 'importTemplate',
        data: await importTemplate({
          template_data: readCommandValue(messageToSend, data, ['template_data', 'templateData']),
          skip_duplicates: readCommandValue(messageToSend, data, ['skip_duplicates', 'skipDuplicates']) ?? true,
        }),
      };
    case 'importTemplates':
      return {
        command: 'importTemplates',
        data: await importTemplates({
          templates_data: readCommandValue(messageToSend, data, ['templates_data', 'templatesData']),
          skip_duplicates: readCommandValue(messageToSend, data, ['skip_duplicates', 'skipDuplicates']) ?? true,
        }),
      };
    case 'createProjectFromTemplate': {
      const templateId = readCommandValue(messageToSend, data, ['template_id', 'templateId']);
      return {
        command: 'createProjectFromTemplate',
        data: await createProjectFromTemplate(templateId, {
          title: readCommandValue(messageToSend, data, ['title']) || '',
          description: readCommandValue(messageToSend, data, ['description']) || '',
          goal: readCommandValue(messageToSend, data, ['goal']) || '',
          folder_path: readCommandValue(messageToSend, data, ['folder_path', 'folderPath']) || '',
        }),
      };
    }
    case 'deleteTimeEvent': {
      const eventId = readCommandValue(messageToSend, data, ['id', 'event_id', 'eventId']);
      return { command: 'deleteTimeEvent', data: await deleteTimeevent(eventId) };
    }
    case 'confirmEvent': {
      const eventId = readCommandValue(messageToSend, data, ['id', 'event_id', 'eventId']);
      const confirm = readCommandValue(messageToSend, data, ['confirm', 'isConfirmed']);
      return { command: 'confirmEvent', data: await updateTimeevent(eventId, { confirm }) };
    }
    case 'updateHideForEvent': {
      const eventId = readCommandValue(messageToSend, data, ['id', 'event_id', 'eventId']);
      const hide = readCommandValue(messageToSend, data, ['hide']);
      return { command: 'updateHideForEvent', data: await updateTimeevent(eventId, { hide }) };
    }
    case 'reanalyzeAllErrorTimeevents': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      return {
        command: 'reanalyzeAllErrorTimeevents',
        data: await reanalyzeProjectErrorTimeevents(caseId),
      };
    }
    case 'linkProjectAsTimeEvent': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId']);
      const linkedCaseId = readCommandValue(messageToSend, data, ['linkedCaseId', 'linked_case_id', 'projectId']);
      return {
        command: 'linkProjectAsTimeEvent',
        data: await linkProjectAsTimeevent(caseId, linkedCaseId),
      };
    }
  case 'getWidgets':
      return { command: 'getWidgets', data: await fetchProjectWidgets(data.caseid ?? messageToSend.caseid) };
    case 'addWidget': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      return {
        command: 'addWidget',
        data: await addWidget(caseId, {
          title: readCommandValueAllowEmpty(messageToSend, data, ['title']),
          variable: readCommandValueAllowEmpty(messageToSend, data, ['variable']),
          description: readCommandValueAllowEmpty(messageToSend, data, ['description']),
          variables: readCommandValue(messageToSend, data, ['variables']) || [],
          use_timeevents: readCommandValue(messageToSend, data, ['use_timeevents', 'useTimeevents']),
          is_placeholder: readCommandValue(messageToSend, data, ['is_placeholder', 'isPlaceholder']),
          visible: readCommandValue(messageToSend, data, ['visible']),
          header_color: readCommandValueAllowEmpty(messageToSend, data, ['header_color', 'headerColor']),
        }),
      };
    }
    case 'updateWidget': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const widgetId = readCommandValue(messageToSend, data, ['widget_id', 'widgetId']);
      return {
        command: 'updateWidget',
        data: await updateWidget(caseId, widgetId, {
          title: readCommandValueAllowEmpty(messageToSend, data, ['title']),
          variable: readCommandValueAllowEmpty(messageToSend, data, ['variable']),
          description: readCommandValueAllowEmpty(messageToSend, data, ['description']),
          variables: readCommandValue(messageToSend, data, ['variables']) || [],
          use_timeevents: readCommandValue(messageToSend, data, ['use_timeevents', 'useTimeevents']),
          is_placeholder: readCommandValue(messageToSend, data, ['is_placeholder', 'isPlaceholder']),
          visible: readCommandValue(messageToSend, data, ['visible']),
          header_color: readCommandValueAllowEmpty(messageToSend, data, ['header_color', 'headerColor']),
          skip_rebuild: readCommandValue(messageToSend, data, ['skip_rebuild', 'skipRebuild']),
        }),
      };
    }
    case 'deleteWidget': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const widgetId = readCommandValue(messageToSend, data, ['widget_id', 'widgetId']);
      return { command: 'deleteWidget', data: await deleteWidget(caseId, widgetId) };
    }
    case 'reanalyzeWidget': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const widgetId = readCommandValue(messageToSend, data, ['widget_id', 'widgetId']);
      return {
        command: 'reanalyzeWidget',
        data: await reanalyzeWidget(caseId, widgetId, {
          reanalyze_scope: readCommandValue(messageToSend, data, ['reanalyze_scope', 'reanalyzeScope']),
          timeevent_ids: readCommandValue(messageToSend, data, ['timeevent_ids', 'timeeventIds']),
        }),
      };
    }
    case 'rebuildWidgetFromProofs': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const widgetId = readCommandValue(messageToSend, data, ['widget_id', 'widgetId']);
      return {
        command: 'reanalyzeWidget',
        data: await rebuildWidgetFromProofs(caseId, widgetId, {
          rebuild_ui: readCommandValue(messageToSend, data, ['rebuild_ui', 'rebuildUi']),
          force_data_schema_rebuild: readCommandValue(messageToSend, data, ['force_data_schema_rebuild', 'forceDataSchemaRebuild']),
        }),
      };
    }
    case 'revertWidgetSnapshot': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const widgetId = readCommandValue(messageToSend, data, ['widget_id', 'widgetId']);
      return {
        command: 'revertWidgetSnapshot',
        data: await revertWidgetSnapshot(caseId, widgetId, {
          snapshot_log_id: readCommandValue(messageToSend, data, ['snapshot_log_id', 'snapshotLogId']),
          snapshot_payload: readCommandValue(messageToSend, data, ['snapshot_payload', 'snapshotPayload']),
        }),
      };
    }
    case 'applyWidgetFeedback': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const widgetId = readCommandValue(messageToSend, data, ['widget_id', 'widgetId']);
      return {
        command: 'reanalyzeWidget',
        data: await applyWidgetFeedback(caseId, widgetId, {
          feedback: readCommandValueAllowEmpty(messageToSend, data, ['feedback']),
          evolved_description: readCommandValueAllowEmpty(messageToSend, data, ['evolved_description', 'evolvedDescription']),
          force_data_schema_rebuild: readCommandValue(messageToSend, data, ['force_data_schema_rebuild', 'forceDataSchemaRebuild']),
        }),
      };
    }
    case 'getDashboardSnapshots': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const limit = readCommandValue(messageToSend, data, ['limit']);
      return { command: 'getDashboardSnapshots', data: await fetchDashboardSnapshots(caseId, { limit }) };
    }
    case 'saveDashboardSnapshot': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const comment = readCommandValue(messageToSend, data, ['comment']);
      return { command: 'saveDashboardSnapshot', data: await saveDashboardSnapshot(caseId, { comment }) };
    }
    case 'loadDashboardSnapshot': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const snapshotId = readCommandValue(messageToSend, data, ['snapshot_id', 'snapshotId']);
      return { command: 'loadDashboardSnapshot', data: await loadDashboardSnapshot(caseId, snapshotId) };
    }
    case 'saveWidgetPreferences': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const widgetId = readCommandValue(messageToSend, data, ['widget_id', 'widgetId']);
      const preferences = readCommandValue(messageToSend, data, ['preferences']) || {};
      const height = readCommandValue(messageToSend, data, ['height']);
      return { command: 'saveWidgetPreferences', data: await saveWidgetPreferences(caseId, widgetId, { preferences, height }) };
    }
    case 'saveWidgetOrder': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      return { command: 'saveWidgetOrder', data: await saveWidgetOrder(caseId, { ...messageToSend, ...data }) };
    }
    case 'downloadAllProjectUploadFiles': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      return { command: 'downloadAllProjectUploadFiles', data: await downloadProjectUploadFiles(caseId) };
    }
    case 'exportexcel_from_variable': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const variable = readCommandValue(messageToSend, data, ['variable']);
      return {
        command: 'exportexcel_from_variable',
        data: await fetchProjectExport(caseId, 'excel/variable', { variable }, 'export.xlsx'),
      };
    }
    case 'exportexcel_all_variables': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      return {
        command: 'exportexcel_all_variables',
        data: await fetchProjectExport(caseId, 'excel/all-variables', {}, 'export_all_variables.xlsx'),
      };
    }
    case 'exportexcel': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const columns = readCommandValue(messageToSend, data, ['columns', 'selectedColumns', 'tags', 'selectedTags']);
      return {
        command: 'exportexcel',
        data: await fetchProjectExport(caseId, 'excel', { columns }, 'export.xlsx'),
      };
    }
    case 'exportcsv': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      return { command: 'exportcsv', data: await fetchProjectExport(caseId, 'csv', {}, 'export.csv') };
    }
    case 'exportpdf': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      return { command: 'exportpdf', data: await fetchProjectExport(caseId, 'pdf', {}, 'export.pdf') };
    }
    case 'exportjson': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      return { command: 'exportjson', data: await fetchProjectExport(caseId, 'json', {}, 'export.json') };
    }
    case 'get_project_tags':
      return { command: 'project_tags', data: await fetchProjectTags(data.caseid ?? messageToSend.caseid) };
    case 'get_all_unique_tags':
      return { command: 'get_all_unique_tags', data: await fetchAllUniqueTags(data.caseid ?? messageToSend.caseid) };
    case 'listGenericDatasources': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const connectorType = readCommandValue(messageToSend, data, ['type', 'connectorType', 'connector_type']);
      return { command: 'listGenericDatasources', data: await fetchGenericDatasources(caseId, connectorType) };
    }
    case 'listAllGenericDatasources': {
      const caseId = readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']);
      const payload = await fetchAllGenericDatasources(caseId, data.types ?? messageToSend.types ?? []);
      const messages = Array.isArray(payload?.messages) ? payload.messages : [];
      if (messages.length > 0) {
        return messages.map((message) => ({
          command: message.command || 'listGenericDatasources',
          data: message.data,
        }));
      }
      return { command: 'listAllGenericDatasources', data: payload };
    }
    case 'getDatasourceSuggestions':
      return {
        command: 'getDatasourceSuggestions',
        data: await fetchDatasourceSuggestions(readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId'])),
      };
    case 'getGenericSourceConnectorStatus':
      return {
        command: 'getGenericSourceConnectorStatus',
        data: await fetchConnectorStatus(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['type', 'connectorType', 'connector_type']),
          readCommandValue(messageToSend, data, ['datasource_id', 'datasourceId'])
        ),
      };
    case 'getGenericSourceConnectorData':
      return {
        command: 'getGenericSourceConnectorData',
        data: await fetchConnectorData(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['type', 'connectorType', 'connector_type']),
          readCommandValue(messageToSend, data, ['datasource_id', 'datasourceId']),
          readCommandValue(messageToSend, data, ['force_new', 'forceNew']) ?? false
        ),
      };
    case 'getDatasourceIngestions':
      return {
        command: 'getDatasourceIngestions',
        data: await fetchDatasourceIngestions(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['datasource_id', 'datasourceId']),
          readCommandValue(messageToSend, data, ['type', 'connectorType', 'connector_type']),
          readCommandValue(messageToSend, data, ['limit'])
        ),
      };
    case 'getDatasourceMonitoringStatus':
      return {
        command: 'getDatasourceMonitoringStatus',
        data: await fetchDatasourceMonitoringStatus(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['datasource_id', 'datasourceId']),
          readCommandValue(messageToSend, data, ['type', 'connectorType', 'connector_type'])
        ),
      };
    case 'getActionPlugins':
      return {
        command: 'getActionPlugins',
        data: await fetchActionPlugins(),
      };
    case 'getListExportsSettings':
      return {
        command: 'getListExportSettings',
        data: await fetchProjectActionExportSettings(readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId'])),
      };
    case 'getExportSettings':
      return {
        command: 'getExportSettings',
        data: await fetchSingleProjectActionExportSettings(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['export_id', 'exportId', 'id'])
        ),
      };
    case 'getFoldersSettings':
      return {
        command: 'getFoldersSettings',
        data: await fetchFoldersSettings(readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId'])),
      };
    case 'addProjectToCollection':
      return {
        command: 'addProjectToCollection',
        data: await addProjectToCollection(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['collection_id', 'collectionId'])
        ),
      };
    case 'removeProjectFromCollection':
      return {
        command: 'removeProjectFromCollection',
        data: await removeProjectFromCollection(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['collection_id', 'collectionId'])
        ),
      };
    case 'enableFolders':
      return {
        command: 'enableFolders',
        data: await updateFoldersEnabled(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['enable'])
        ),
      };
    case 'saveFoldersSettings':
      return {
        command: 'saveFoldersSettings',
        data: await saveFoldersSettings(
          readCommandValue(messageToSend, data, ['caseid', 'case_id', 'caseId', 'project_id', 'projectId']),
          readCommandValue(messageToSend, data, ['folders']) || []
        ),
      };
    case 'get_saved_models': {
      const payload = await fetchSavedModels();
      return { command: 'saved_models', data: payload.saved_models ?? payload };
    }
    case 'get_text_model_settings':
    case 'get_vision_model_settings':
    case 'get_audio_model_settings':
    case 'get_performance_settings':
    case 'get_format_settings': {
      const payload = await fetchConfigurationData();
      return { command, data: payload.configuration_data ?? payload };
    }
    case 'get_available_ollama_models':
      return { command: 'available_ollama_models', data: await fetchOllamaModels() };
    case 'get_server_info':
      return { command: 'server_info', data: await fetchServerInfo(data) };
    case 'get_active_tasks_info':
      return { command: 'active_tasks_info', data: await fetchAdminActiveTasksInfo(data) };
    case 'get_postprocessing_status':
      return { command: 'postprocessing_status', data: await fetchPostprocessingStatus() };
    case 'get_logs':
      return { command: 'get_logs_response', data: await fetchAdminLogs(data) };
    case 'configuration_check': {
      const payload = await fetchConfigurationData();
      return { command: 'configuration_data', data: payload.configuration_data ?? payload };
    }
    case 'update_configuration':
      {
        const payload = await updateConfigurationData(data);
        return { command: 'update_configuration', data: payload?.status ?? payload };
      }
    default:
      return null;
  }
};
